import React, { useEffect, useRef, useState } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import '../config';

interface VideoInfo {
  id: string;
  status: string;
  url: string;
  processed_at?: string;
  error?: string;
}

const VideoPlayer: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const videoRef = useRef<HTMLVideoElement>(null);
  const [videoInfo, setVideoInfo] = useState<VideoInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchVideoInfo = async () => {
      if (!id) return;
      
      try {
        const response = await axios.get(`/api/v1/video/${id}/status`);
        const data = response.data.data;
        
        if (data.status === 'completed') {
          setVideoInfo(data);
        } else if (data.status === 'failed') {
          setError(data.error || 'Video processing failed');
        } else {
          setError('Video is still processing');
        }
      } catch (err) {
        setError('Failed to load video information');
        console.error('Error fetching video info:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchVideoInfo();
  }, [id]);

  useEffect(() => {
    const startTime = searchParams.get('start');
    if (videoRef.current && startTime && videoInfo) {
      const seconds = parseFloat(startTime);
      if (!isNaN(seconds)) {
        console.log('Setting video start time to:', seconds);
        
        // Set the time once the video is ready for seeking
        const handleCanPlay = () => {
          if (videoRef.current) {
            videoRef.current.currentTime = seconds;
            console.log('Video time set to:', videoRef.current.currentTime);
          }
        };
        
        if (videoRef.current.readyState >= 2) {
          // Video is ready for seeking
          handleCanPlay();
        } else {
          // Wait for canplay event instead of loadedmetadata
          videoRef.current.addEventListener('canplay', handleCanPlay, { once: true });
        }
      }
    }
  }, [searchParams, videoInfo]);

  if (loading) {
    return (
      <div style={{ padding: '20px', textAlign: 'center' }}>
        <p>Loading video...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: '20px', textAlign: 'center' }}>
        <p style={{ color: 'red' }}>{error}</p>
        <button 
          onClick={() => navigate('/')}
          style={{
            marginTop: '20px',
            padding: '10px 20px',
            fontSize: '16px',
            backgroundColor: '#007bff',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer'
          }}
        >
          Back to Search
        </button>
      </div>
    );
  }

  if (!videoInfo || !id) {
    return (
      <div style={{ padding: '20px', textAlign: 'center' }}>
        <p>Video not found</p>
      </div>
    );
  }

  const videoUrl = `/api/v1/video/${id}`;
  const startTime = searchParams.get('start');
  const endTime = searchParams.get('end');
  
  // Use the basic video URL without timestamp parameters
  // The client will handle seeking to the start time
  const finalVideoUrl = videoUrl;

  return (
    <div style={{ padding: '20px', maxWidth: '1200px', margin: '0 auto' }}>
      <button 
        onClick={() => navigate('/')}
        style={{
          marginBottom: '20px',
          padding: '8px 16px',
          fontSize: '14px',
          backgroundColor: '#6c757d',
          color: 'white',
          border: 'none',
          borderRadius: '4px',
          cursor: 'pointer'
        }}
      >
        ← Back to Search
      </button>
      
      <div style={{ backgroundColor: '#000', borderRadius: '8px', overflow: 'hidden' }}>
        <video
          ref={videoRef}
          controls
          autoPlay
          style={{ width: '100%', maxHeight: '70vh' }}
          src={finalVideoUrl}
          onError={(e) => {
            console.error('Video error:', e);
            setError('Failed to load video');
          }}
          onLoadStart={() => console.log('Video loading started')}
          onLoadedMetadata={() => console.log('Video metadata loaded')}
          onCanPlay={() => console.log('Video can play')}
        >
          Your browser does not support the video tag.
        </video>
      </div>

      <div style={{ marginTop: '20px', padding: '20px', backgroundColor: '#f5f5f5', borderRadius: '8px' }}>
        <h3>Video Information</h3>
        <p><strong>ID:</strong> {videoInfo.id}</p>
        <p><strong>Status:</strong> {videoInfo.status}</p>
        {videoInfo.processed_at && (
          <p><strong>Processed:</strong> {new Date(videoInfo.processed_at).toLocaleString()}</p>
        )}
        {startTime && (
          <p><strong>Start Time:</strong> {formatTime(parseFloat(startTime))}</p>
        )}
        {endTime && (
          <p><strong>End Time:</strong> {formatTime(parseFloat(endTime))}</p>
        )}
      </div>
    </div>
  );
};

function formatTime(seconds: number): string {
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}

export default VideoPlayer;